import { useMemo, useState } from 'react'
import type { Category, Item, Recipe } from '../../lib/supabase'
import {
  addTag, coverModel, ingredientCount, recipeNeeds, removeTag, tagSuggestions, type Need, type TagCount,
} from '../../lib/recipeBook'
import type { Choice } from '../../lib/choices'
import { ingredientAmount } from '../../lib/recipe'
import { recipeStatus, scaleFactorFor } from '../../lib/recipeMatch'
import { refreshSummary } from '../../lib/recipeRefresh'
import { isVegetarian } from '../../lib/vegetarian'
import { safeHttpUrl } from '../../lib/url'
import type { RecipePatch, RefreshOutcome } from '../../hooks/useRecipes'
import { SheetPanel } from '../SheetPanel'
import { Stepper } from '../ui/Stepper'
import { Switch } from '../ui/Switch'
import { ChoicesSheet } from '../choices/ChoicesSheet'
import { BasketIcon, CloseIcon, ExternalIcon, PencilIcon, PlayIcon, StarIcon, TimerIcon } from '../icons'
import { Cover } from './Cover'
import { isLongTitle } from '../../lib/title'

export interface RecipeDetailProps {
  recipe: Recipe
  items: Item[]
  householdTags: TagCount[]
  categories: Category[]
  categoryFor: (name: string) => string | null
  /** The household's own name for an ingredient (useItemHistory); optional for tests. */
  spellingFor?: (name: string) => string
  /** Names bought before, suggested when a row of the choices sheet is edited (lib/catalog.ts). */
  before?: { name: string }[]
  tintFor: (categoryId: string | null) => string
  /** "koket.se" or "Added by Anna". */
  source: string
  /** Opened from a planned meal: its servings. */
  initialServings?: number
  locale?: string
  locked: boolean
  onUpdate: (patch: RecipePatch) => void
  onDelete: () => void
  /** Opens the recipe editor on this recipe. */
  onEdit: () => void
  /** Its photo's signed URL (useRecipePhotos). */
  photoUrl?: string | null
  onApply: (needs: Need[], choices: Choice[], note: string) => void
  /** Starts cooking mode at the servings shown. */
  onCook: (servings: number) => void
  /** useRecipes.refreshRecipe for this recipe: fills what it lacks from its link. */
  onRefresh?: () => Promise<RefreshOutcome>
  onUpgrade: () => void
  onClose: () => void
}

const SERVINGS_MIN = 1
const SERVINGS_MAX = 12
const DEFAULT_SERVINGS = 4

const REFRESH_ERRORS: Record<Exclude<RefreshOutcome, { ok: true }>['error'], string> = {
  no_recipe: 'The page no longer has a recipe we can read.',
  text_no_recipe: 'The page no longer has a recipe we can read.',
  fetch_failed: "The page didn't answer. Try again in a moment.",
  rate_limited: 'Lots of imports just now. Try again in a minute.',
  needs_caption: 'An Instagram post can only be read from its caption. Edit the recipe instead.',
  failed: "The page didn't load. Try again in a moment.",
  subscription_required: 'Recipes are part of Plus. Get Plus to change them.',
  source_blocked: 'This site has asked not to be imported. Edit the recipe instead.',
}

// One recipe (feature spec 02 "Recipe detail"): a bottom sheet on the phone, the
// 900 px dialog on desktop. A page to read first: the dish, its ingredients at
// the servings chosen (each marked if it is already on the list) and the
// method. Adding to the list and cooking are actions at the foot; the choices
// open in their own sheet. Edits here change the saved recipe only through the
// star, tags, the vegetarian switch and Refresh from link.
export function RecipeDetail(p: RecipeDetailProps) {
  const r = p.recipe
  const base = r.servings
  const [servings, setServings] = useState<number | null>(p.initialServings ?? base)
  const [tagDraft, setTagDraft] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [shopping, setShopping] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshNote, setRefreshNote] = useState<{ text: string; error: boolean } | null>(null)
  const needs = useMemo(() => recipeNeeds(r, servings, p.spellingFor), [r, servings, p.spellingFor])
  const status = useMemo(() => recipeStatus(r, p.items, servings), [r, p.items, servings])
  const factor = scaleFactorFor(r, servings)
  const cover = coverModel(r, p.categoryFor, p.categories)
  const href = safeHttpUrl(r.url)
  const write = (patch: RecipePatch) => (p.locked ? p.onUpgrade() : p.onUpdate(patch))
  const names = p.householdTags.map(t => t.name)
  const suggestions = tagSuggestions(r.tags, p.householdTags)

  function commitTag(raw: string) {
    const next = addTag(r.tags, raw, names)
    setTagDraft('')
    if (next !== r.tags) write({ tags: next })
  }

  async function refresh() {
    if (!p.onRefresh || refreshing) return
    if (p.locked) { p.onUpgrade(); return }
    setRefreshing(true)
    setRefreshNote(null)
    const out = await p.onRefresh()
    setRefreshing(false)
    setRefreshNote(out.ok ? { text: refreshSummary(out.filled), error: false } : { text: REFRESH_ERRORS[out.error], error: true })
  }

  const amountOf = (i: number) => ingredientAmount(r.ingredients[i], factor, p.locale)
  const statusOf = (i: number) => {
    const line = status.lines[i]
    return line.item ? (line.bought ? 'In basket' : 'On list') : null
  }
  const shoppingRows = r.ingredients.map((_, i) => i).filter(i => !r.ingredients[i].pantry)
  const pantryRows = r.ingredients.map((_, i) => i).filter(i => r.ingredients[i].pantry)
  const row = (i: number) => {
    const s = statusOf(i)
    return (
      <li key={i} className={`ml-detail__ing${s ? ' is-covered' : ''}`}>
        <span className="ml-detail__amount ml-num">{amountOf(i)}</span>
        <span className="ml-detail__ing-name">{r.ingredients[i].name}</span>
        {s && <span className="ml-detail__have">{s}</span>}
      </li>
    )
  }

  return (
    <>
    <SheetPanel onClose={p.onClose} maxHeight="94dvh" layout="scroll" ariaLabel={r.title} wide>
      <div className="ml-detail">
        <header className="ml-detail__top">
          <span className="ml-label ml-detail__source">
            {/* Credit where the recipe came from, linked back to it. */}
            {href && p.source
              ? <a className="ml-detail__source-link" href={href} target="_blank" rel="noopener noreferrer">From {p.source}</a>
              : p.source}
            {r.rating_value != null && r.rating_count != null && (
              <span className="ml-detail__rating">
                <StarIcon size={12} filled />{r.rating_value.toFixed(1)} of 5, {r.rating_count} {r.rating_count === 1 ? 'rating' : 'ratings'}
              </span>
            )}
          </span>
          <div className="ml-detail__actions">
            <button type="button" className="ml-detail__icon-btn" aria-pressed={r.starred} aria-label={`Star ${r.title}`}
              onClick={() => write({ starred: !r.starred })}>
              <StarIcon size={22} filled={r.starred} />
            </button>
            <button type="button" className="ml-detail__icon-btn" aria-label={`Edit ${r.title}`}
              onClick={() => (p.locked ? p.onUpgrade() : p.onEdit())}>
              <PencilIcon size={20} />
            </button>
            <button type="button" className="ml-btn ml-btn--secondary" onClick={p.onClose}>Close</button>
          </div>
        </header>

        <h2 className={`ml-display ml-detail__title${isLongTitle(r.title) ? ' is-long' : ''}`}>{r.title}</h2>

        <div className="ml-detail__facts">
          <Cover cover={cover} tintFor={p.tintFor} size="tile" photoUrl={p.photoUrl} />
          {r.minutes != null && (
            <div className="ml-detail__stat"><span className="ml-detail__num ml-num">{r.minutes}</span><span className="ml-label">Minutes</span></div>
          )}
          <div className="ml-detail__stat"><span className="ml-detail__num ml-num">{ingredientCount(r)}</span><span className="ml-label">Ingredients</span></div>
          {r.steps.length > 0 && (
            <div className="ml-detail__stat"><span className="ml-detail__num ml-num">{r.steps.length}</span><span className="ml-label">Steps</span></div>
          )}
        </div>

        <div className="ml-detail__row">
          <span className="ml-label ml-detail__row-label">Servings</span>
          {servings != null && base != null ? (
            <Stepper value={servings} min={SERVINGS_MIN} max={Math.max(SERVINGS_MAX, base)} label="Servings" onChange={setServings} />
          ) : <span className="ml-caption">Not given. Amounts as written.</span>}
        </div>
        {servings != null && base != null && servings !== base && (
          <p className="ml-detail__scaled">
            Amounts scaled from {base}{' '}
            <button type="button" className="ml-detail__reset" onClick={() => setServings(base)}>Reset</button>
          </p>
        )}

        <div className="ml-detail__body">
          <section className="ml-detail__section" aria-label="Ingredients">
            <h3 className="ml-label ml-detail__heading">
              Ingredients
              {status.total > 0 && <span className="ml-detail__count">{status.covered} of {status.total} on your list</span>}
            </h3>
            {r.ingredients.length === 0
              ? <p className="ml-caption">No ingredients saved yet.</p>
              : <ul className="ml-detail__ings">{shoppingRows.map(row)}</ul>}
            {pantryRows.length > 0 && (
              <>
                <h4 className="ml-label ml-detail__subheading">From the pantry</h4>
                <ul className="ml-detail__ings ml-detail__ings--pantry">{pantryRows.map(row)}</ul>
              </>
            )}
          </section>

          <section className="ml-detail__section" aria-label="Method">
            <h3 className="ml-label ml-detail__heading">Method</h3>
            {r.steps.length === 0 ? (
              <p className="ml-caption">
                {r.url ? 'No method saved. Refresh from link to fetch it, or write it with Edit.' : 'No method saved. Add the steps with Edit.'}
              </p>
            ) : (
              <ol className="ml-detail__steps">
                {r.steps.map((s, i) => (
                  <li key={i} className="ml-detail__step">
                    <span className="ml-detail__step-num ml-num" aria-hidden="true">{i + 1}</span>
                    <span className="ml-detail__step-text">
                      {s.text}
                      {s.minutes != null && (
                        <span className="ml-detail__timer"><TimerIcon size={14} /> {s.minutes} min</span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <div className="ml-detail__row ml-detail__row--top">
          <span className="ml-label ml-detail__row-label">Tags</span>
          <div className="ml-detail__tags">
            {r.tags.map(t => (
              <span key={t} className="ml-tag-chip">
                {t}
                <button type="button" className="ml-tag-chip__x" aria-label={`Remove tag ${t}`} onClick={() => write({ tags: removeTag(r.tags, t) })}>
                  <CloseIcon size={14} />
                </button>
              </span>
            ))}
            <input className="ml-tag-input" aria-label="Add tag" placeholder="Add tag" maxLength={24} value={tagDraft}
              onChange={e => {
                const v = e.target.value
                if (v.includes(',')) commitTag(v.split(',')[0])
                else setTagDraft(v)
              }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitTag(tagDraft) } }} />
            {suggestions.length > 0 && (
              <span className="ml-detail__suggestions" role="group" aria-label="Suggested tags">
                {suggestions.map(t => (
                  <button key={t} type="button" className="ml-tag-suggest" onClick={() => commitTag(t)}>+ {t}</button>
                ))}
              </span>
            )}
          </div>
        </div>

        <Switch label="Vegetarian" description="Shown as a tag and under the Vegetarian filter"
          checked={isVegetarian(r)} onChange={v => write({ vegetarian: v })} />

        <div className="ml-detail__cta">
          <button type="button" className="ml-btn ml-btn--secondary ml-detail__cta-btn"
            onClick={() => p.onCook(servings ?? base ?? DEFAULT_SERVINGS)}>
            <PlayIcon size={18} /> Cook
          </button>
          <button type="button" className="ml-btn ml-btn--primary ml-detail__cta-btn" disabled={needs.length === 0}
            onClick={() => (p.locked ? p.onUpgrade() : setShopping(true))}>
            <BasketIcon size={18} /> Add to list
          </button>
        </div>

        {refreshNote && (
          <p className={refreshNote.error ? 'ml-detail__note is-error' : 'ml-detail__note'} role={refreshNote.error ? 'alert' : 'status'}>
            {refreshNote.text}
          </p>
        )}

        <footer className="ml-detail__footer">
          <span className="ml-detail__links">
            {href && (
              <a className="ml-text-btn" href={href} target="_blank" rel="noopener noreferrer">
                Open recipe page <ExternalIcon size={16} />
              </a>
            )}
            {href && p.onRefresh && (
              <button type="button" className="ml-text-btn" disabled={refreshing} onClick={refresh}>
                {refreshing ? 'Refreshing…' : 'Refresh from link'}
              </button>
            )}
          </span>
          {confirmDelete ? (
            <button type="button" className="ml-text-btn ml-text-btn--destructive"
              onClick={() => { p.onDelete(); p.onClose() }}>Tap again to delete</button>
          ) : (
            <button type="button" className="ml-text-btn ml-text-btn--quiet" onClick={() => setConfirmDelete(true)}>Delete recipe</button>
          )}
        </footer>
      </div>
    </SheetPanel>

      <ChoicesSheet before={p.before} open={shopping} zIndex={20} kicker="Shopping for" title={r.title}
        chips={servings != null ? [`${servings} servings`] : []} needs={needs} items={p.items} source="recipe" locale={p.locale}
        onApply={(edited, choices) => { p.onApply(edited, choices, r.title); setShopping(false); p.onClose() }}
        onClose={() => setShopping(false)} />
    </>
  )
}
